import { HubsApi, Configuration as HubsConfiguration } from '@control-center/control-center-client-public-hubs-ts';
import {
    MissionsApi,
    Configuration as MissionsConfiguration,
} from '@control-center/control-center-client-public-missions-ts';
import { Configuration, VehiclesApi } from '@control-center/control-center-client-public-vehicles-ts';
import { fetchAuthSession } from 'aws-amplify/auth';
import axios, { type RawAxiosRequestConfig } from 'axios';
import { v4 as uuid } from 'uuid';

const REQUEST_TIMEOUT_MS = 30_000;

/** One correlation id per request, for cross-service tracing. Shared by every generated client. */
export const correlationHeaders = (): RawAxiosRequestConfig => ({ headers: { 'x-correlation-id': uuid() } });

export class RestError extends Error {
    readonly status: number;
    readonly body: unknown;

    constructor(message: string, status: number, body?: unknown) {
        super(message);
        this.name = 'RestError';
        this.status = status;
        this.body = body;
    }
}

/** Shared by every generated REST client here: one Cognito access token per request. */
export const getServiceAccessToken = async (): Promise<string> => {
    const token = (await fetchAuthSession()).tokens?.accessToken?.toString();
    if (!token) {
        throw new RestError('No Cognito access token available', 401);
    }
    return token;
};

const configuration = new Configuration({
    basePath: import.meta.env.VITE_VEHICLE_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const vehiclesApi = new VehiclesApi(configuration);

/**
 * Separate Configuration instance from the *missions* package, so the two clients keep
 * independent basePaths even though the two generated Configuration classes are identical.
 *
 * Only the configuration is passed — no basePath 2nd arg, no axios instance 3rd arg. Handing a
 * root axios instance to a generated client is what produces the pre-existing dual-axios type
 * errors in `src/features/domain-simulation/apiClient.ts`.
 */
const missionConfiguration = new MissionsConfiguration({
    basePath: import.meta.env.VITE_MISSION_HUB_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const missionsApi = new MissionsApi(missionConfiguration);

/**
 * Hubs share the mission-and-hub service host, but get their own Configuration instance for the
 * same reason missions do: three generated `Configuration` classes are now in play, all identically
 * named and all mutually unassignable, so each client is handed the one from its own package.
 *
 * Exported as `hubsRestApi`, not `hubsApi`: `services/hubs.ts` already exports `hubsApi` for the
 * RTK Query slice, and that file imports this one.
 */
const hubConfiguration = new HubsConfiguration({
    basePath: import.meta.env.VITE_MISSION_HUB_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    accessToken: getServiceAccessToken,
    baseOptions: {
        timeout: REQUEST_TIMEOUT_MS,
        headers: { accept: 'application/json' },
    },
});

export const hubsRestApi = new HubsApi(hubConfiguration);

/**
 * Hand-rolled because no published monitoring client exists; its one operation
 * (`GET /api/v1/health/status`) declares no response body, so only the status code is read.
 * Named `monitoringApi` since `systemApi` is already the RTK Query slice in `services/system.ts`.
 */
export const monitoringApi = axios.create({
    baseURL: import.meta.env.VITE_MONITORING_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
});

monitoringApi.interceptors.request.use(async config => {
    config.headers.set('Authorization', `Bearer ${await getServiceAccessToken()}`);
    config.headers.set('Accept', 'application/json');

    // Remove the axios default Content-Type on requests without a body to avoid triggering CORS
    // preflights the backend doesn't handle, as `features/domain-simulation/apiClient.ts` does.
    if (!config.data) {
        config.headers.delete('Content-Type');
    }

    return config;
});

/**
 * Hand-rolled because no published management client exists (every `npm view` lookup returned E404).
 * Named `managementRestApi`, not `managementApi`: that name is already taken by the
 * `DomainSimulationManagementApi` instance in `features/domain-simulation/apiClient.ts`.
 */
export const managementRestApi = axios.create({
    baseURL: import.meta.env.VITE_MANAGEMENT_SERVICE_ENDPOINT?.replace(/\/+$/, ''),
    timeout: REQUEST_TIMEOUT_MS,
});

managementRestApi.interceptors.request.use(async config => {
    config.headers.set('Authorization', `Bearer ${await getServiceAccessToken()}`);
    config.headers.set('Accept', 'application/json');

    // Remove the axios default Content-Type on requests without a body to avoid triggering CORS
    // preflights the backend doesn't handle, as `monitoringApi` above does.
    if (!config.data) {
        config.headers.delete('Content-Type');
    }

    return config;
});
